<template>
  <v-card class="qa-filter-card mt-3" variant="flat">
    <div class="d-flex align-center flex-wrap ga-3 pa-3">
      <!-- ช่องทาง -->
      <v-btn-toggle :model-value="channel" density="comfortable" variant="outlined" divided mandatory rounded="lg" color="primary" @update:model-value="update('channel', $event)">
        <v-btn value="all" size="small">{{ $t('qaAllChannels') }}</v-btn>
        <v-btn value="voice" size="small" prepend-icon="mdi-phone">{{ $t('qaVoice') }}</v-btn>
        <v-btn value="chat" size="small" prepend-icon="mdi-message-text">{{ $t('qaChat') }}</v-btn>
      </v-btn-toggle>

      <!-- หัวข้อของเคส -->
      <div class="d-flex align-center ga-2">
        <v-select
          :model-value="topic"
          :items="topicItems"
          item-title="label"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          :loading="topicsLoading"
          style="max-width: 260px"
          prepend-inner-icon="mdi-shape-outline"
          @update:model-value="update('topic', $event)"
        />
        <!-- ⚠️ โหลดสารบัญหัวข้อไม่สำเร็จต้อง**เห็น** — ของเดิม catch{} เงียบ ทำให้ dropdown
             เหลือแต่ "ทุกหัวข้อ" ซึ่งหน้าตาเหมือนระบบยังไม่มีหัวข้อเลย คนเลยไม่แจ้งใคร -->
        <v-tooltip v-if="topicError" location="bottom" :text="topicError">
          <template #activator="{ props }">
            <v-icon v-bind="props" color="error" size="20">mdi-alert-circle-outline</v-icon>
          </template>
        </v-tooltip>
      </div>

      <!-- ช่วงเวลา -->
      <v-select
        :model-value="range"
        :items="rangeItems"
        item-title="label"
        item-value="value"
        density="compact"
        variant="outlined"
        hide-details
        style="max-width: 190px"
        prepend-inner-icon="mdi-calendar-range"
        @update:model-value="update('range', $event)"
      />

      <v-spacer />
      <slot />
    </div>
  </v-card>
</template>

<script>
/**
 * แถบตัวกรองร่วมของ QA Console (ช่องทาง / หัวข้อ / ช่วงเวลา)
 *
 * ค่าอยู่ใน Vuex module `qaConsole` ไม่ใช่ใน component เพื่อให้ค้างข้ามหน้า
 * — เลือก 90 วันในหน้า Overview แล้วกดเข้า Case Explorer ต้องไม่ต้องเลือกใหม่
 *
 * ⚠️ ค่าของ dropdown หัวข้อคือ **topicId ของจริง** จาก GET topics (สารบัญเดียวกับที่
 *    QaCaseClassifier ใช้ติดหัวข้อให้เคส) · ของเดิมเอา `categoryId` จาก taxonomy จำลอง
 *    มาเป็นค่า ซึ่งเป็นคนละแกนกับ `TopicId` ที่เคสจริงเก็บไว้ → เลือกหัวข้อไหนก็ได้ 0 แถว
 *    ตลอด และไม่มีใครสงสัยเพราะ "ไม่มีเคสในหัวข้อนี้" เป็นคำตอบที่ฟังขึ้น
 *
 * ⚠️ `range` เป็น "จำนวนวัน" และถูกแปลงเป็น `days` ให้ server คิด from เอง ที่ชั้น service
 *    ไม่ใช่ที่นี่ — อย่าคำนวณวันที่ในหน้าจอ (timezone ของเบราว์เซอร์กับของ server คนละตัว)
 */
import qaConsole from '@/services/qaConsole';

export default {
  name: 'QaFilterBar',
  emits: ['change'],
  data() {
    return {
      topicItems: [{ label: this.$t('qaAllTopics'), value: 'all' }],
      topicsLoading: false,
      topicError: '',
    };
  },
  computed: {
    channel() {
      return this.$store.state.qaConsole.channel;
    },
    topic() {
      return this.$store.state.qaConsole.topic;
    },
    range() {
      return this.$store.state.qaConsole.range;
    },
    rangeItems() {
      return [
        { label: this.$t('qaLast7Days'), value: 7 },
        { label: this.$t('qaLast30Days'), value: 30 },
        { label: this.$t('qaLast90Days'), value: 90 },
        { label: this.$t('qaAllTime'), value: 9999 },
      ];
    },
  },
  async mounted() {
    this.topicsLoading = true;
    try {
      const res = await qaConsole.getTopics();
      this.topicItems = [
        { label: this.$t('qaAllTopics'), value: 'all' },
        ...(res.items || []).map((t) => ({ label: t.label, value: t.topicId })),
      ];
      this.topicError = '';
    } catch (err) {
      // ยังกรองด้วยช่องทาง/ช่วงเวลาได้ (ไม่ล้มทั้งหน้า) แต่ต้องติดป้ายไว้ว่าตัวกรองหัวข้อพัง
      this.topicItems = [{ label: this.$t('qaAllTopics'), value: 'all' }];
      this.topicError = `โหลดสารบัญหัวข้อไม่สำเร็จ — กรองตามหัวข้อไม่ได้ชั่วคราว (${err.message || err})`;
    } finally {
      this.topicsLoading = false;
    }
  },
  methods: {
    update(key, value) {
      if (value === null || value === undefined) return;
      this.$store.commit('qaConsole/SET_FILTER', { [key]: value });
      this.$emit('change');
    },
  },
};
</script>

<style scoped>
.qa-filter-card {
  border: 1px solid #e1e1e1;
  border-radius: 16px;
}
</style>
